import { NativeStackNavigationProp, NativeStackScreenProps } from "@react-navigation/native-stack";
import type { CompositeScreenProps } from "@react-navigation/native";
import type { BottomTabScreenProps } from "@react-navigation/bottom-tabs";
import { CountryCode } from "react-native-country-picker-modal";
import { Activity } from "./fragments";
import { EmploymentsToRemove } from "./AddEmploymentScreen/types";

// route types
export type RootStackParamList = {
  Activities: { tag: string } | undefined;
  Activity: { code: string };
  ActivityReviewList: undefined;
  ActivityReview: { id: string };
  ActivityRequestPoints: { code: string; id?: string };

  // My Activity
  MyActivity: { id: string; name?: string };
  MyActivityHistory: { plan?: string } | undefined;
  MyActivityManageRequest: { id: string };

  ComponentLibrary: undefined;
  Enroll: undefined;
  Main: undefined;
  DeferredComp: undefined;
  Login: undefined;
  Onboarding: undefined;
  Welcome: undefined;
  Account: undefined;
  AccountEdit: undefined;
  AccountEditContact: undefined;
  EmailChange: undefined;
  SoftLanding: { memberType?: string };
  Consent: undefined;
  EmploymentRemove: { id: string; nextAction?: string; newPrimaryEmploymentId?: string };
  EmploymentEdit: { id: string };
  CurrentEmploymentReview: undefined;
  AddEmployment: { employmentsToRemove?: EmploymentsToRemove[]; returnTo?: () => void };
  ChangeEmploymentType: undefined;
  UpdateEmployment: { employmentId: string };
  SelectPrimaryEmployment: { employmentToRemove: string };

  // Jobs
  JobPreferences: undefined;
  JobDetail: { id: string };
  JobApply: { id: string; companyName: string };
  JobApplySuccess: { companyName: string };

  // Events
  EventDetails: { slug: string };
  EventRegistration: { slug: string };
  EventConfirmation: { slug: string };
  EventTicket: { slug: string };

  // Dues
  Dues: { assignProxy: boolean; orderId?: string; showProxyModal?: boolean } | undefined;
  DuesProxyPayment: { id: string };
  DuesSummary: undefined;
  AssignProxy: undefined;

  // Videos
  Videos: undefined;
  VideosDetails: { id: string };
  // Admin
  AssumeUser: undefined;

  PdrHistory: { slug: string };
  PdrDetails: { id: string };
  ReportPDR: { slug: string };

  InvoiceList: { slug: string };
};

export type ScreenProps<name extends keyof RootStackParamList> = NativeStackScreenProps<RootStackParamList, name, "root">;

export type RootStackScreenProps<T extends keyof RootStackParamList> = NativeStackScreenProps<RootStackParamList, T>;

export type MainTabParamList = {
  DeferredComp: undefined;
  Account: undefined;
  Home: undefined;
  Jobs: undefined;
};

export type MainTabScreenProps<T extends keyof MainTabParamList> = CompositeScreenProps<
  BottomTabScreenProps<MainTabParamList, T>,
  RootStackScreenProps<keyof RootStackParamList>
>;

declare global {
  namespace ReactNavigation {
    interface RootParamList extends RootStackParamList {}
  }
}

export type NavigationProps = NativeStackNavigationProp<RootStackParamList>;

interface Level {
  id: string;
  requirements: string;
  points: number;
}
export interface ActivityDetails extends Activity {
  description: string;
  participationLevels: [Level];
}

export interface Reviewer {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  profilePhoto?: string;
  location?: string;
}

export enum SubmissionStatus {
  APPROVED = "APPROVED",
  CANCELLED = "CANCELLED",
  EXPIRED = "EXPIRED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
}

export enum EligibilityStatus {
  NON_COACH = "NON_COACH",
  NON_MEMBER = "NON_MEMBER",
  INELIGIBLE_CLASS = "INELIGIBLE_CLASS",
  INACTIVE = "INACTIVE",
  EMPLOYEE = "EMPLOYEE",
  ELIGIBLE = "ELIGIBLE",
}

export type AddressType = "home";

export interface Address {
  id: string;
  address1: string;
  address2?: string;
  addressType: AddressType;
  city: string;
  countryCode: string;
  isVerified?: boolean;
  postalCode?: string;
  state?: string;
  addressable: Addressable;
}

export type Addressable = Facility | Person;

type Section = {
  id: string;
  name: string;
};

export type Person = {
  id: string;
  addresses: Address[];
  phoneNumbers: PersonPhone[];
  contactAddress?: Address;
  employments: Employment[];
  pendingChangeRequests?: PendingChangeRequest[];
  primarySectionAffiliation?: {
    id: string;
    section: Section;
  };
  primarySection?: Section;
  __typename: "CRMPerson";
};

export type PendingChangeRequest = {
  id: string;
  status: string;
  createdAt: string;
  details: ChangeRequestDetails;
  facility: Facility;
};

type ChangeRequestDetails = {
  type: string;
};

export type Facility = {
  id: string;
  name: string;
  addresses: Address[];
  section: Section;
  __typename: "CRMFacility";
};

export type PersonPhone = {
  id: string;
  phoneNumber: string;
  phoneType: "home" | "mobile";
  phoneCountry: CountryCode;
  phoneFormatted: string;
};

export enum WorkingLocation {
  ON_SITE = "ON_SITE",
  REMOTE = "REMOTE",
}

export interface Employment {
  id: string;
  startedOn: string;
  endedOn?: string;
  facility: Facility;
  title: string;
  isPrimary: boolean;
  description?: string;
  jobClassification?: string;
  workingLocation: WorkingLocation;
}
